// ** React Imports
import { memo, useCallback, useMemo } from "react";

// ** Redux & Store & Actions
import { useSelector } from "react-redux";

// ** Third Party Components
import { useTranslation } from "react-i18next";
import CountUp from "react-countup";

// ** Custom Components
import Block from "components/layout/Block";
import Section from "components/layout/Section";
import DecorBox from "components/layout/DecorBox";

// ** Third Party Components
import styled, { css } from "styled-components";

// ** Utils & Helpers
import pseudoFormat from "utils/pseudoFormat";

// #####################################################

const StyledCustomBlock = styled(Block)`
	${({ $minWidth }) =>
		$minWidth &&
		css`
			min-width: ${$minWidth};
		`}

	.decimalNumber {
		display: inline-block;
		min-width: ${({ $counterWidth }) => $counterWidth}; // "X,Y mln" width
		text-align: right;
	}
`;

const StatisticsSection = () => {
	const { t } = useTranslation(["common", "landing"]);

	const language = useSelector((state) => state.globalCommon.language);
	const usersOnline = useSelector(
		(state) => state.globalCommon.statistics.usersOnline
	);
	const mediaTotal = useSelector(
		(state) => state.globalCommon.statistics.mediaTotal
	);
	const registeredUsers = useSelector(
		(state) => state.globalCommon.statistics.usersTotal
	);

	const isClient = useSelector((state) => state.globalCommon.isClient);

	const formatValue = useCallback(
		(value) => {
			return `${pseudoFormat(
				Math.floor((value / 1000000) * 10) / 10
			)} ${t("common:numbersShort:milion")}`;
		},
		[language]
	);

	const registeredUsersValue =
		Math.floor((registeredUsers / 1000000) * 10) / 10;
	const registeredUsersIsDecimal = registeredUsersValue % 1 !== 0;

	const mediaTotalValue = Math.floor((mediaTotal / 1000000) * 10) / 10;
	const mediaTotalIsDecimal = mediaTotalValue % 1 !== 0;

	// IMPORTANT: When changing font size, margins, etc., you must recalculate the values.
	// TODO: Find a way to automatically calculate the largest width.
	const counterWidth = useMemo(() => {
		switch (language) {
			case "cs":
				return "100px";
			case "de":
				return "109px";
			case "en":
				return "82px";
			case "pl":
				return "110px";
			case "ru":
				return "117px";
			case "uk":
				return "117px";
			default:
				return "110px";
		}
	}, [language]);

	const renderStats = useMemo(() => {
		return (
			<Block flex flexWrap>
				<DecorBox
					topText={t("siteStats.currently")}
					mainText={
						<StyledCustomBlock $minWidth="98px">
							{isClient ? (
								<CountUp
									separator=" "
									enableScrollSpy
									scrollSpyOnce
									end={usersOnline > 0 ? usersOnline : 1}
									duration={2}
									start={1}
									update={usersOnline > 0 ? usersOnline : 1}
									preserveValue
								>
									{({ countUpRef }) => (
										<span ref={countUpRef} />
									)}
								</CountUp>
							) : (
								usersOnline
							)}
						</StyledCustomBlock>
					}
					bottomText={t("siteStats.usersOnline")}
				/>
				<DecorBox
					topText={t("siteStats.moreThan")}
					mainText={
						<StyledCustomBlock
							$minWidth="84px"
							$counterWidth={counterWidth}
						>
							{isClient ? (
								<CountUp
									separator=" "
									enableScrollSpy
									scrollSpyOnce
									end={
										registeredUsers > 0
											? registeredUsers
											: 100000
									}
									duration={2}
									formattingFn={formatValue}
									start={100000}
								>
									{({ countUpRef }) => (
										<span
											ref={countUpRef}
											className={
												registeredUsersIsDecimal
													? "decimalNumber"
													: ""
											}
										/>
									)}
								</CountUp>
							) : (
								registeredUsers
							)}
						</StyledCustomBlock>
					}
					bottomText={t("siteStats.registeredUsers")}
				/>
				<DecorBox
					topText={t("siteStats.moreThan")}
					mainText={
						<StyledCustomBlock
							$minWidth="110px"
							$counterWidth={counterWidth}
						>
							{isClient ? (
								<CountUp
									separator=" "
									enableScrollSpy
									scrollSpyOnce
									end={mediaTotal > 0 ? mediaTotal : 100000}
									duration={2}
									formattingFn={formatValue}
									start={100000}
								>
									{({ countUpRef }) => (
										<span
											ref={countUpRef}
											className={
												mediaTotalIsDecimal
													? "decimalNumber"
													: ""
											}
										/>
									)}
								</CountUp>
							) : (
								mediaTotal
							)}
						</StyledCustomBlock>
					}
					bottomText={t("siteStats.addedMedia")}
				/>
			</Block>
		);
	}, [registeredUsers, registeredUsers, mediaTotal, language]);

	// #####################################################

	return <Section>{renderStats}</Section>;
};

// #####################################################

export default memo(StatisticsSection);

// #####################################################
